import { useMemo, useState } from 'react';
import { useAddOmnisendEventMutation, useGetProductPricesQuery } from '../../../features/quizApiSlice';
import useMeta from '../../../hooks/useMeta';
import { waitForTracking } from '../../../utils/tracking';
import {
  OMEGA3_DURATION_TO_PLAN_KEY,
  OMEGA3_PLAN_KEYS,
  OMEGA3_PRODUCT_TYPE,
} from '../data/omega3Data';

const PLAN_ORDER = [
  OMEGA3_PLAN_KEYS.MONTHLY_SUBSCRIPTION,
  OMEGA3_PLAN_KEYS.QUARTERLY_SUBSCRIPTION,
  OMEGA3_PLAN_KEYS.ONE_MONTH_ONE_OFF,
  OMEGA3_PLAN_KEYS.THREE_MONTH_ONE_OFF,
];

const SUBSCRIPTION_DISCOUNT_BY_PLAN = {
  [OMEGA3_PLAN_KEYS.MONTHLY_SUBSCRIPTION]: 0.1,
  [OMEGA3_PLAN_KEYS.QUARTERLY_SUBSCRIPTION]: 0.3,
};

const TARGET_AMOUNT_BY_PLAN = {
  [OMEGA3_PLAN_KEYS.MONTHLY_SUBSCRIPTION]: 24.3,
  [OMEGA3_PLAN_KEYS.QUARTERLY_SUBSCRIPTION]: 56.7,
  [OMEGA3_PLAN_KEYS.ONE_MONTH_ONE_OFF]: 27,
  [OMEGA3_PLAN_KEYS.THREE_MONTH_ONE_OFF]: 64.8,
};

function applySubscriptionDiscount(planKey, amount) {
  const discount = SUBSCRIPTION_DISCOUNT_BY_PLAN[planKey] || 0;
  if (!discount) return amount;
  return Math.round(amount * (1 - discount) * 10) / 10;
}

const PLAN_META = {
  [OMEGA3_PLAN_KEYS.MONTHLY_SUBSCRIPTION]: {
    titleKey: 'Omega3_Plan_Monthly_Title',
    badgeKey: 'Omega3_Plan_Monthly_Badge',
    detailKeys: [
      'Omega3_Plan_Monthly_Detail_1',
      'Omega3_Plan_Monthly_Detail_2',
      'Omega3_Plan_Monthly_Detail_3',
    ],
    recurring: true,
    quantityLabelKey: 'Omega3_Plan_Monthly_Quantity',
    ctaLabelKey: 'Omega3_Cta_Subscribe',
    fallbackAmount: 27,
  },
  [OMEGA3_PLAN_KEYS.QUARTERLY_SUBSCRIPTION]: {
    titleKey: 'Omega3_Plan_Quarterly_Title',
    badgeKey: 'Omega3_Plan_Quarterly_Badge',
    detailKeys: [
      'Omega3_Plan_Quarterly_Detail_1',
      'Omega3_Plan_Quarterly_Detail_2',
      'Omega3_Plan_Quarterly_Detail_3',
    ],
    recurring: true,
    quantityLabelKey: 'Omega3_Plan_Quarterly_Quantity',
    ctaLabelKey: 'Omega3_Cta_Subscribe',
    fallbackAmount: 81,
  },
  [OMEGA3_PLAN_KEYS.ONE_MONTH_ONE_OFF]: {
    titleKey: 'Omega3_Plan_OneOffMonthly_Title',
    badgeKey: 'Omega3_Plan_OneOffMonthly_Badge',
    detailKeys: [
      'Omega3_Plan_OneOffMonthly_Detail_1',
      'Omega3_Plan_OneOffMonthly_Detail_2',
      'Omega3_Plan_OneOffMonthly_Detail_3',
    ],
    recurring: false,
    quantityLabelKey: 'Omega3_Plan_OneOffMonthly_Quantity',
    ctaLabelKey: 'Omega3_Cta_BuyNow',
    fallbackAmount: 27,
  },
  [OMEGA3_PLAN_KEYS.THREE_MONTH_ONE_OFF]: {
    titleKey: 'Omega3_Plan_OneOffQuarterly_Title',
    badgeKey: 'Omega3_Plan_OneOffQuarterly_Badge',
    detailKeys: [
      'Omega3_Plan_OneOffQuarterly_Detail_1',
      'Omega3_Plan_OneOffQuarterly_Detail_2',
      'Omega3_Plan_OneOffQuarterly_Detail_3',
    ],
    recurring: false,
    quantityLabelKey: 'Omega3_Plan_OneOffQuarterly_Quantity',
    ctaLabelKey: 'Omega3_Cta_BuyNow',
    fallbackAmount: 81,
  },
};


function buildCheckoutUrl(paymentLink) {
  const email = localStorage.getItem('email');
  const params = new URLSearchParams();

  if (email) {
    params.set('prefilled_email', email);
  }

  return params.toString() ? `${paymentLink}?${params.toString()}` : paymentLink;
}

function buildOmega3OmnisendSubscription(plan) {
  return {
    title: plan?.titleKey || 'Omega-3',
    description: plan?.duration || 'Omega-3',
    duration: plan?.duration || null,
    recurring: Boolean(plan?.recurring),
    product_type: OMEGA3_PRODUCT_TYPE,
  };
}

export default function useOmega3Purchase({ source = 'product_page' } = {}) {
  const { metaEvent } = useMeta();
  const { data, isLoading, isFetching } = useGetProductPricesQuery({
    product_type: OMEGA3_PRODUCT_TYPE,
  });
  const [addOmnisendEventMutation] = useAddOmnisendEventMutation();
  const [selectedPlanKey, setSelectedPlanKey] = useState(OMEGA3_PLAN_KEYS.MONTHLY_SUBSCRIPTION);

  const plans = useMemo(() => {
    const rows = Array.isArray(data?.data) ? data.data : [];
    const map = rows.reduce((acc, row) => {
      if (!row?.payment_link) return acc;

      const planKey = OMEGA3_DURATION_TO_PLAN_KEY[row.duration];
      if (!planKey) return acc;

      const quantity = planKey === OMEGA3_PLAN_KEYS.QUARTERLY_SUBSCRIPTION || planKey === OMEGA3_PLAN_KEYS.THREE_MONTH_ONE_OFF ? 3 : 1;
      const meta = PLAN_META[planKey];
      const baseAmount = Number(row.amount || meta.fallbackAmount || 0);
      const targetAmount = TARGET_AMOUNT_BY_PLAN[planKey] ?? applySubscriptionDiscount(planKey, baseAmount);
      const amount = targetAmount;
      const amountDiff = Math.abs(baseAmount - targetAmount);
      const isThreeMonthPlan = quantity === 3;
      if (isThreeMonthPlan && amountDiff > 0.001) return acc;

      const candidatePlan = {
        ...meta,
        id: planKey,
        amount,
        currency: 'EUR',
        duration: row.duration,
        quantity,
        productType: row.product_type,
        paymentLink: row.payment_link,
        perMonthAmount: quantity > 1 ? amount / quantity : amount,
      };

      if (!acc[planKey]) {
        acc[planKey] = {
          ...candidatePlan,
          _sourceAmount: baseAmount,
          _amountDiff: amountDiff,
        };
        return acc;
      }

      const currentDiff = acc[planKey]._amountDiff ?? Number.POSITIVE_INFINITY;
      const currentSourceAmount = acc[planKey]._sourceAmount ?? Number.POSITIVE_INFINITY;

      if (amountDiff < currentDiff || (amountDiff === currentDiff && baseAmount < currentSourceAmount)) {
        acc[planKey] = {
          ...candidatePlan,
          _sourceAmount: baseAmount,
          _amountDiff: amountDiff,
        };
      }

      return acc;
    }, {});

    return PLAN_ORDER.map((key) => {
      const matchedPlan = map[key];
      if (matchedPlan) {
        const { _sourceAmount, _amountDiff, ...cleanPlan } = matchedPlan;
        return cleanPlan;
      }

      return {
        ...PLAN_META[key],
        id: key,
        amount: TARGET_AMOUNT_BY_PLAN[key] ?? applySubscriptionDiscount(key, PLAN_META[key].fallbackAmount),
        currency: 'EUR',
        quantity: key.includes('quarterly') ? 3 : 1,
        productType: OMEGA3_PRODUCT_TYPE,
        paymentLink: null,
        perMonthAmount: key.includes('quarterly')
          ? (TARGET_AMOUNT_BY_PLAN[key] ?? applySubscriptionDiscount(key, PLAN_META[key].fallbackAmount)) / 3
          : (TARGET_AMOUNT_BY_PLAN[key] ?? applySubscriptionDiscount(key, PLAN_META[key].fallbackAmount)),
      };
    });
  }, [data]);

  const selectedPlan = plans.find((plan) => plan.id === selectedPlanKey) || plans[0] || null;

  const startCheckout = async () => {
    if (!selectedPlan?.paymentLink) return;

    const checkoutUrl = buildCheckoutUrl(selectedPlan.paymentLink);
    const email = localStorage.getItem('email');

    await waitForTracking([
      metaEvent('AddToCart'),
      metaEvent('InitiateCheckout'),
      email
        ? addOmnisendEventMutation({
            email,
            url: checkoutUrl,
            subscribtion: buildOmega3OmnisendSubscription(selectedPlan),
            price: selectedPlan.amount,
          })
            .unwrap()
            .catch((error) => console.log('Error event ', error))
        : null,
    ]);

    sessionStorage.setItem(
      'omega3_checkout_context',
      JSON.stringify({
        source,
        productType: OMEGA3_PRODUCT_TYPE,
        plan: selectedPlan.id,
      }),
    );

    window.location.href = checkoutUrl;
  };

  return {
    isLoading: isLoading || isFetching,
    plans,
    selectedPlan,
    selectedPlanKey,
    setSelectedPlanKey,
    startCheckout,
  };
}
